'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { ColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { Plus, Trash, ArrowUp, ArrowDown, Info } from '@phosphor-icons/react';

interface SortField {
  column: string;
  order: 'asc' | 'desc';
  columnType?: string;
}

interface SortConfigProps {
  nodeId: string;
  config: {
    fields?: SortField[];
  };
}

export function SortConfig({ nodeId, config }: SortConfigProps) {
  const { updateNodeConfig } = useWorkflowStore();
  const [fields, setFields] = useState<SortField[]>(
    config.fields || [{ column: '', order: 'asc' }]
  );
  
  // 获取输入节点的列（从连线的前一个节点）
  const inputColumns = useNodeInputColumns(nodeId);

  const updateField = (index: number, updates: Partial<SortField>) => {
    const newFields = fields.map((f, i) =>
      i === index ? { ...f, ...updates } : f
    );
    setFields(newFields);
    updateNodeConfig(nodeId, {
      type: 'sort',
      params: { fields: newFields },
    });
  };

  const addField = () => {
    const newFields = [...fields, { column: '', order: 'asc' as const }];
    setFields(newFields);
    updateNodeConfig(nodeId, {
      type: 'sort',
      params: { fields: newFields },
    });
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    updateNodeConfig(nodeId, {
      type: 'sort',
      params: { fields: newFields },
    });
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === fields.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newFields = [...fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    
    setFields(newFields);
    updateNodeConfig(nodeId, {
      type: 'sort',
      params: { fields: newFields },
    });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-zinc-700">排序配置</div>
      
      <div className="space-y-3">
        <AnimatePresence>
          {fields.map((field, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-3"
            >
              {/* Priority Badge */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-500">
                  优先级 {index + 1}
                </span>
                {fields.length > 1 && (
                  <div className="flex items-center gap-1">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => moveField(index, 'up')}
                      disabled={index === 0}
                      className="p-1.5 text-zinc-500 hover:bg-white hover:shadow-sm rounded-lg disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => moveField(index, 'down')}
                      disabled={index === fields.length - 1}
                      className="p-1.5 text-zinc-500 hover:bg-white hover:shadow-sm rounded-lg disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => removeField(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash className="w-3.5 h-3.5" />
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Column Selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-500">选择列</label>
                <ColumnSelector
                  value={field.column}
                  onChange={(columnName, columnInfo) => updateField(index, { 
                    column: columnName,
                    columnType: columnInfo?.type 
                  })}
                  placeholder={inputColumns ? '选择要排序的列...' : '请先连接输入节点'}
                  columns={inputColumns}
                />
              </div>
              
              {/* Order Toggle */}
              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    updateField(index, {
                      order: field.order === 'asc' ? 'desc' : 'asc',
                    })
                  }
                  className={`
                    flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all
                    ${
                      field.order === 'asc'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-zinc-100 border-zinc-200 text-zinc-600 hover:bg-zinc-200'
                    }
                  `}
                >
                  {field.order === 'asc' ? (
                    <>
                      <ArrowUp className="w-3.5 h-3.5" weight="bold" />
                      升序 (A → Z)
                    </>
                  ) : (
                    <>
                      <ArrowDown className="w-3.5 h-3.5" weight="bold" />
                      降序 (Z → A)
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={addField}
          className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          添加排序字段
        </motion.button>
      </div>
      
      {/* Info Tip */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 leading-relaxed">
          排序优先级按列表顺序，先按第一个字段排序，相同则按第二个字段排序，以此类推。
        </p>
      </div>
    </div>
  );
}
